import "./LoadingProgress.scss";

import LoadingIcon from "components/Loading/LoadingIcon";
import React, { useEffect, useState } from "react";

type LoadingProgressProps = {
  messages: string[];
  isLoading: boolean;
  className?: string;
  position?: "fixed" | "relative";
};

const LoadingProgress: React.FC<LoadingProgressProps> = ({
  messages,
  isLoading,
  className = "",
  position = "fixed",
}) => {
  const [currentLoadingMessage, setCurrentLoadingMessage] = useState<number>(0);
  const [hasCompletedCycle, setHasCompletedCycle] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [slowdownPoint] = useState(() => Math.floor(Math.random() * 20) + 60); // random point between 60-80%

  // fake progress bar animation
  useEffect(() => {
    if (!isLoading) {
      setProgress(0);
      setHasCompletedCycle(false);
      setCurrentLoadingMessage(0);
      return;
    }

    // start with quick progress then slow down
    const initialProgress = setInterval(() => {
      setProgress((prev) => {
        if (prev < slowdownPoint) {
          // Random increment between 1-3
          const increment = Math.random() * 2 + 1;
          return Math.min(prev + increment, slowdownPoint);
        }
        return prev;
      });
    }, 100);

    // slower progress after slowdown point
    const slowProgress = setInterval(() => {
      setProgress((prev) => {
        if (prev >= slowdownPoint && prev < 90) {
          // random small increment between 0.1-0.8
          const increment = Math.random() * 0.7 + 0.1;
          return Math.min(prev + increment, 90);
        }
        return prev;
      });
    }, 500);

    return () => {
      clearInterval(initialProgress);
      clearInterval(slowProgress);
    };
  }, [isLoading, slowdownPoint]);

  // rotate loading messages once
  useEffect(() => {
    if (!isLoading || hasCompletedCycle) return;

    const interval = setInterval(() => {
      setCurrentLoadingMessage((prev) => {
        const nextMessage = prev + 1;
        if (nextMessage >= messages.length - 1) {
          setHasCompletedCycle(true);
          clearInterval(interval);
          return messages.length - 1;
        }
        return nextMessage;
      });
    }, 6000);

    return () => clearInterval(interval);
  }, [isLoading, hasCompletedCycle, messages.length]);

  if (!isLoading) return null;

  return (
    <div className={`LoadingProgress ${position} ${className}`}>
      <LoadingIcon message="Loading..." />
      <div className="loading-sub-text fade">
        {messages[currentLoadingMessage]}
      </div>
      <div className="loading-progress">
        <div className="loading-progress-bar" style={{ width: `${progress}%` }}>
          <span className="progress-text">{progress.toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
};

export default LoadingProgress;
